import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Search, Store } from "lucide-react";
import client, { money } from "@/lib/api";
import { addToList } from "@/pages/Home";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const CATS = ["All", "Dairy & Eggs", "Produce", "Meat & Seafood", "Bakery", "Pantry", "Frozen", "Snacks", "Beverages", "Household"];

function ProductCard({ p, i }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`catalog-product-${i}`} className="bg-white rounded-2xl p-3 pp-shadow flex flex-col transition-transform duration-200 hover:-translate-y-1">
      <div className="h-36 rounded-xl bg-white grid place-items-center overflow-hidden">
        {p.image ? <img src={p.image} alt={p.name} loading="lazy" className="max-h-full max-w-full object-contain" /> : <Store className="h-8 w-8 text-pp-border" />}
      </div>
      <p className="mt-2 text-[11px] font-bold uppercase tracking-wide text-pp-muted truncate">{p.brand || "\u00a0"}</p>
      <p className="text-sm font-bold text-pp-text line-clamp-2 min-h-[2.5rem]">{p.name}</p>
      <p className="text-xs text-pp-muted truncate">{[p.size, p.category && p.category !== "All" ? p.category : null].filter(Boolean).join(" · ")}</p>
      <div className="mt-1.5 flex items-baseline gap-1.5 flex-wrap">
        <span className="text-lg font-extrabold text-pp-forest">{money(p.best.price)}</span>
        {p.best.was_price && <span className="text-xs text-pp-muted line-through">{money(p.best.was_price)}</span>}
      </div>
      <p className="text-[11px] text-pp-muted truncate">at {p.best.store_banner}{p.best.unit_price ? ` · ${p.best.unit_price}` : ""}</p>
      {p.offers.length > 1 && (
        <button type="button" onClick={() => setOpen(!open)} className="mt-1 text-left text-[11px] font-bold text-pp-forest" data-testid={`catalog-compare-${i}`}>
          {p.save_vs_highest > 0 ? `Save ${money(p.save_vs_highest)} vs other store` : `Same price at ${p.offers.length} stores`} {open ? "▲" : "▼"}
        </button>
      )}
      {open && p.offers.map((o) => (
        <div key={o.store_banner} className="flex justify-between text-[11px] text-pp-text"><span className="truncate">{o.store_banner}</span><b>{money(o.price)}</b></div>
      ))}
      <button type="button" onClick={() => addToList({ name: `${p.brand} ${p.name} ${p.size}`.trim() })} data-testid={`catalog-add-${i}`}
        className="mt-auto pt-2"><span className="block w-full rounded-full bg-pp-forest text-white text-xs font-bold py-1.5 transition-transform duration-150 hover:scale-[1.02]">+ Add to my list</span></button>
    </div>
  );
}

export default function CatalogBrowser({ initialQuery }) {
  const [q, setQ] = useState(initialQuery || "");
  const [cat, setCat] = useState(initialQuery ? null : "Dairy & Eggs");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (params) => {
    setBusy(true);
    try { const { data: d } = await client.get("/catalog/search", { params }); setData(d); }
    catch { setData({ products: [], stores: [] }); } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(initialQuery ? { q: initialQuery } : { category: "Dairy & Eggs" }); }, [load, initialQuery]);

  const submit = (e) => { e.preventDefault(); if (q.trim().length >= 2) { setCat(null); load({ q: q.trim() }); } };
  const pickCat = (c) => { setCat(c); setQ(""); load({ category: c }); };

  return (
    <section className="space-y-4" data-testid="catalog-browser">
      <form onSubmit={submit} className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-pp-muted" />
        <Input data-testid="catalog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search every product — brand names included"
          className="h-12 pl-12 pr-28 rounded-2xl bg-white border-0 pp-shadow" />
        <Button type="submit" data-testid="catalog-search-button" className="absolute right-1.5 top-1.5 h-9 rounded-xl bg-pp-forest">Search</Button>
      </form>
      <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
        {CATS.map((c) => (
          <button key={c} type="button" onClick={() => pickCat(c)} data-testid={`catalog-cat-${c.replace(/\W+/g, "-").toLowerCase()}`} title={c === "All" ? "Every category combined" : c}
            className={`shrink-0 rounded-full px-4 py-2 text-sm font-bold transition-colors ${cat === c ? "bg-pp-forest text-white" : "bg-white text-pp-text pp-shadow hover:bg-pp-cream"}`}>{c === "All" ? "Everything" : c}</button>
        ))}
      </div>
      {data?.stores?.length > 0 && (
        <p className="text-xs text-pp-muted" data-testid="catalog-stores">Live shelf prices from {data.stores.map((s) => s.name).join(" and ")} · more stores coming</p>
      )}
      {busy ? <div className="grid place-items-center py-10"><Loader2 className="h-6 w-6 animate-spin text-pp-forest" /></div> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {(data?.products || []).map((p, i) => <ProductCard key={`${p.brand}-${p.name}-${p.size}`} p={p} i={i} />)}
          {data && !data.products?.length && <p className="col-span-full text-sm text-pp-muted py-6 text-center">No products found — try another search.</p>}
        </div>
      )}
    </section>
  );
}
